import { useContext } from 'react'
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { ProfileContext } from 'components/Profile/ProfileContext'
import { TrashIcon, EnvelopeIcon, UserMinusIcon, SquaresPlusIcon, PencilSquareIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { GetAthleteInviteToSignUpWithCoachPlainText } from 'functions/textUtils'
import { useGetCoaches } from 'redux/customHooks/useGetCoaches';
import { UsersRound } from "lucide-react";
import { WhichWeightAPI } from 'WhichWeightAPI';
import { resetOrganizationGroups } from 'redux/slices/organization';
import { AppIsInTestEnvironment } from 'functions/environmentUtils'
import trainingPeaksAppIcon from 'media/integrations/training_peaks/TrainingPeaks_App_Icon.jpg'

import UserProfilePicture from 'components/User/UserProfilePicture'
import TabSelector from 'components/Tabs/TabSelector';

export default function KinesisProfileHeader({ 
    groupTabs = [],
    individualTabs = [], 
    selectedTabKey,
    HandleTabChange = () => {} 
}) {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const coaches = useGetCoaches()

    const { ShowModal } = useContext(ModalContext)
    const { ReloadTrainingChartData } = useContext(ProfileContext)

    const profileType = useSelector(state => state.athleteProfile.profileType)
    const userId = useSelector(state => state.user.id)
    const organizationId = useSelector(state => state.user.organizationId)
    const organizationName = useSelector(state => state.user.organizationName)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const groupName = useSelector(state => state.athleteProfile.groupName)
    const groupDescription = useSelector(state => state.athleteProfile.groupDescription)
    const groupAthletes = useSelector(state => state.athleteProfile.groupAthletes)
    const firstName = useSelector(state => state.athleteProfile.firstName)
    const lastName = useSelector(state => state.athleteProfile.lastName)
    const email = useSelector(state => state.athleteProfile.email)
    const coachFirstName = useSelector(state => state.user.firstName)
    const coachLastName = useSelector(state => state.user.lastName)
    const coachInviteSignUpCode = useSelector(state => state.athleteProfile.coachInviteSignUpCode)
    const trainingPeaksMappingAssignerCoachId = useSelector(state => state.athleteProfile.trainingPeaksMappingAssignerCoachId)
    const trainingPeaksUserId = useSelector(state => state.athleteProfile.trainingPeaksUserId)

    const OnArchiveGroupButton = () => {

        if (groupAthletes.length > 0) {
            const modalData = {
                key: ModalKey.Text,
                size: 'quarter',
                titleText: 'Unable to Delete Group',
                bodyText: [
                    'Please remove all athletes from ' + groupName + ' before deleting it.',
                    'Contact support@kinesisintegrated.com for additional assistance.',
                ]
            }

            ShowModal(modalData)

            return
        }

        const modalData = {
            key: ModalKey.ActionWarning,
            size: 'quarter',
            titleText: 'Delete ' + groupName + '?',
            bodyText: [
                'Are you sure you want to delete ' + groupName + '?',
                'This group will no longer be visible to anyone in your organization'
            ],
            buttonText: 'Delete',
            buttonAction: () => ArchiveGroup()
        }

        ShowModal(modalData)

    }

    const ArchiveGroup = async () => {

        const jsonBody = {
            organizationId: organizationId, // used for authorization
            groupId: groupId
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('REPORTING', 'POST', '/archive_group', jsonBody)

        // console.log(result)

        if (result.data.status === 'failure') {
            console.error(result)
            return
        }

        dispatch(resetOrganizationGroups())

        navigate('/groups')

    }

    const GetTrainingPeaksMappingAssignerCoachName = (trainingPeaksMappingAssignerCoachId) => {
        if (trainingPeaksMappingAssignerCoachId === null) return 'Unknown'

        const coach = coaches.find(coach => coach.id === trainingPeaksMappingAssignerCoachId)
        return coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown'
    }

    const OnTrainingPeaksWorkoutClicked = () => {

        if (trainingPeaksMappingAssignerCoachId !== userId) {

            const modalData = {
                key: ModalKey.Text,
                size: 'quarter',
                titleText: 'Unable to open TrainingPeaks calendar',
                bodyText: [
                    firstName + ' ' + lastName + '\'s TrainingPeaks integration is not setup with your account.',
                    'It was connected to ' + GetTrainingPeaksMappingAssignerCoachName(trainingPeaksMappingAssignerCoachId) + '\'s TrainingPeaks account.',
                    'Please contact us at support@kinesisintegrated.com for more information.'
                ],
                buttonText: 'Close',
                buttonAction: () => {}
            }

            ShowModal(modalData)

            return
        }

        var baseUrl = 'https://app.sandbox.trainingpeaks.com'

        if (process.env.REACT_APP_NODE_ENV === 'prod') baseUrl = 'https://app.trainingpeaks.com'
        else if (process.env.REACT_APP_NODE_ENV === 'test') baseUrl = 'https://app.sandbox.trainingpeaks.com'
        else if (process.env.REACT_APP_NODE_ENV === 'dev') baseUrl = 'https://app.sandbox.trainingpeaks.com'
        else baseUrl = 'https://app.sandbox.trainingpeaks.com'

        window.open(baseUrl + '/#calendar/athletes/' + trainingPeaksUserId.toString(),  '_blank')
    }

    const ShowInviteAthleteEmailPreviewModal = () => {
        const modalData = {
            key: ModalKey.PlainTextEmailPreview,
            size: 'half',
            titleText: 'Invite Email Preview',
            bodyText: [
                'This is a preview of the plain text email that will be sent to the athletes.',
                'Click anywhere below to copy the text to your clipboard.'
            ],
            plainText: GetAthleteInviteToSignUpWithCoachPlainText(coachFirstName, coachLastName, organizationName, coachInviteSignUpCode),
            includeSendFunctionality: true
        }

        ShowModal(modalData)
    }

    const ShowOffboardAthleteWarningModal = () => {
        const modalData = {
            key: ModalKey.ActionWarning,
            size: 'quarter',
            titleText: 'Offboard Athlete?',
            bodyText: [
                'Are you sure you want to offboard this athlete?',
                'You will no longer be able to view this athlete\'s data and they will no longer be able to complete workouts.',
                'Offboards can be done on the billing page.'
            ],
            buttonText: 'Go To Billing Page',
            buttonAction: () => {
                navigate('/settings/billing')
            }
        }

        ShowModal(modalData)
    }

    const OnGroupNameLinkClicked = () => {
        window.open(`/group/${groupId}`, '_blank')
    }

    if (profileType === 'group') {
        return (
            <div>
                <div className='flex items-center gap-x-4 pb-2'>
                    <div>
                        <div className="h-12 w-12 bg-orange-400 rounded-full flex items-center justify-center">
                            <UsersRound className="h-6 w-6 text-white mr-0.5" />
                        </div>
                    </div>

                    <div className='mb-0.5 pr-2'>
                        <h1 className="text-xl font-semibold text-gray-900 tracking-tight flex-grow">
                            {groupName}
                        </h1>

                        <div className="relative group max-w-[300px]">
                            <p className="text-sm max-w-[300px] truncate cursor-pointer">
                                {groupDescription.length > 0 ? groupDescription : 'No Description'}
                            </p>

                            {groupDescription.length > 0 && (
                                <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-pre-wrap w-[300px] text-center">
                                    {groupDescription}
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className='relative group p-2 bg-white rounded-md shadow-md cursor-pointer hover:bg-gray-50'
                        onClick={() => navigate('/groups/edit')}
                    >
                        <PencilSquareIcon className="h-6 w-6 text-gray-600" />

                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                            Edit Group
                        </div>
                    </div>

                    <div
                        className='relative group p-2 bg-white rounded-md shadow-md cursor-pointer hover:bg-gray-50'
                        onClick={OnArchiveGroupButton}
                    >
                        <TrashIcon className="h-6 w-6 text-gray-600" />

                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                            Delete Group
                        </div>
                    </div>

                    <div className='flex-1' />

                    {/* Place tab selector in top right corner on larger monitors */}
                    <div className="hidden 2xl:block">
                        <TabSelector tabs={groupTabs} selectedTabKey={selectedTabKey} HandleTabChange={HandleTabChange} />
                    </div>
                </div>

                {/* Place tab selector underneath name on smaller monitors */}
                <div className="block 2xl:hidden pt-2">
                    <TabSelector tabs={groupTabs} selectedTabKey={selectedTabKey} HandleTabChange={HandleTabChange} />
                </div>
            </div>
        )
    }

    if (profileType === 'group-individual' || profileType === 'individual') {
        return (
            <div>
                <div className='flex items-center gap-x-4 pb-2'>
                    <UserProfilePicture firstName={firstName} lastName={lastName} />

                    <div className='mb-0.5 pr-2'>
                        <h1 className="text-xl font-semibold text-gray-900 tracking-tight flex-grow">
                            {firstName} {lastName}
                        </h1>

                        <p className="text-sm">{(email && email.length) > 0 ? email : 'No Email'}</p>
                    </div>

                    <div 
                        className='relative group p-2 bg-white rounded-md shadow-md cursor-pointer hover:bg-gray-50'
                        onClick={() => navigate('/onboarding/athletes/trainingPeaks?defaultValue=no-mapping')}
                    >
                        <SquaresPlusIcon className="h-6 w-6 text-gray-600" />
                        
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                            Manage Athlete Integrations
                        </div>
                    </div>

                    {(email === null || email === undefined || email.length === 0) && (
                        <div 
                            className='relative group p-2 bg-white rounded-md shadow-md cursor-pointer hover:bg-gray-50'
                            onClick={ShowInviteAthleteEmailPreviewModal}
                        >
                            <EnvelopeIcon className="h-6 w-6 text-gray-600" />

                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                                Invite Athlete to Sign In to this Account
                            </div>
                        </div>
                    )}

                    {(email && email.length > 0) && (
                        <div 
                            className='relative group p-2 bg-white rounded-md shadow-md cursor-pointer hover:bg-gray-50'
                            onClick={ShowOffboardAthleteWarningModal}
                        >
                            <UserMinusIcon className="h-6 w-6 text-gray-600" />

                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                                Offboard Athlete
                            </div>
                        </div>
                    )}

                    {(trainingPeaksUserId !== null && trainingPeaksUserId !== undefined && trainingPeaksUserId.length > 0) && (
                        <div 
                            className="relative group cursor-pointer"
                            onClick={OnTrainingPeaksWorkoutClicked}
                        >
                            <img 
                                src={trainingPeaksAppIcon}
                                alt="TrainingPeaks App Icon" 
                                className="h-10 w-10 rounded-md shadow-md" 
                            />

                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                                View TrainingPeaks Calendar
                            </div>
                        </div>
                    )}

                    {AppIsInTestEnvironment() && (
                        <div 
                            className='relative group p-2 bg-white rounded-md shadow-md cursor-pointer hover:bg-gray-50'
                            onClick={() => ReloadTrainingChartData()}
                        >
                            <ArrowPathIcon className="h-6 w-6 text-gray-600" />

                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-center">
                                Reload Training Chart
                            </div>
                        </div>
                    )}

                    <div className='flex-1' />

                    {/* Place tab selector in top right corner on larger monitors */}
                    <div className="hidden 2xl:block">
                        <TabSelector tabs={individualTabs.filter(tab => tab.visible)} selectedTabKey={selectedTabKey} HandleTabChange={HandleTabChange} />
                    </div>
                </div>

                {/* Place tab selector underneath name on smaller monitors */}
                <div className="block 2xl:hidden pt-2">
                    <TabSelector tabs={individualTabs.filter(tab => tab.visible)} selectedTabKey={selectedTabKey} HandleTabChange={HandleTabChange} />
                </div>

                {profileType === 'group-individual' && (
                    <div 
                        className='w-full py-2 text-center bg-orange-400 text-white rounded-lg cursor-pointer mt-4'
                        onClick={OnGroupNameLinkClicked}
                    >
                        <p className='text-sm font-bold'>
                            <>{firstName} {lastName} belongs to {groupName}.</> 
                            {' '}
                            <>Their program is being managed by the group.</>
                            {' '}
                            <span className='text-sm font-bold underline' >View Group</span>
                        </p>
                    </div>
                )}
            </div>
        )
    }
}